import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Building2, Check, ChevronRight, MoreVertical, UserMinus, UserPlus } from 'lucide-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { fetchEscritorio } from '@/api/client';
import {
  incluirParticipante,
  removerParticipante,
  reordenarParticipantes,
  type Configuracao,
  type Participante,
} from '@/api/secretario';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { nomeDoParticipante } from './advogadoEmEdicao';
import { temPreco } from './HonorariosCampos';
import { PADRAO_DO_ESCRITORIO } from './ModelosPadrao';
import { PadraoDoEscritorio } from './PadraoDoEscritorio';
import { PerfilDoAdvogado } from './PerfilDoAdvogado';
import { WizardActions } from './WizardActions';
import { gravarConfigNoCache } from './cacheDaConfig';

interface StepProps {
  config: Configuracao | null;
  onNext: () => void;
  onBack: () => void;
  isFirst: boolean;
}

/**
 * Os advogados por quem o Secretário do escritório fala (docs/PLANO_SECRETARIO_ESCRITORIO.md
 * §4.2): quem participa, em que ordem o cliente os ouve e se cada um está pronto. Tocar
 * num advogado abre o perfil dele; a última linha abre o padrão do escritório. Os dois
 * têm endereço (``?advogado=<id>`` ou ``?advogado=escritorio``): o voltar do navegador e
 * a volta do editor de modelos caem neles.
 */
export function AdvogadosStep({ config, onNext, onBack, isFirst }: StepProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const aberto = searchParams.get('advogado');
  const abrir = (id: string) =>
    setSearchParams(
      (atual) => {
        const proximo = new URLSearchParams(atual);
        proximo.set('tab', 'config');
        proximo.set('passo', 'advogados');
        proximo.set('advogado', id);
        return proximo;
      },
      { state: { daLista: true } },
    );
  // Aberto pela lista, voltar é o voltar do navegador (o histórico não acumula a ida e
  // a volta); pelo link (aviso, editor de modelos), tira o parâmetro.
  const fechar = () => {
    if ((location.state as { daLista?: boolean } | null)?.daLista) {
      navigate(-1);
      return;
    }
    setSearchParams(
      (atual) => {
        const proximo = new URLSearchParams(atual);
        proximo.delete('advogado');
        return proximo;
      },
      { replace: true },
    );
  };

  if (!config) return null;
  if (aberto === PADRAO_DO_ESCRITORIO) return <PadraoDoEscritorio config={config} onVoltar={fechar} />;
  if (aberto) return <PerfilDoAdvogado config={config} advogadoId={aberto} onVoltar={fechar} />;
  const ativos = (config.participantes ?? []).filter((p) => p.ativo);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Advogados</CardTitle>
        <CardDescription>Quando mais de um atende a área do caso, o cliente escolhe, nesta ordem.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ul className="space-y-2">
          {ativos.map((p, i) => (
            <LinhaDoAdvogado
              key={p.usuario_id}
              participante={p}
              indice={i}
              ativos={ativos}
              secretarioAtivo={config.ativo}
              onAbrir={() => abrir(p.usuario_id)}
            />
          ))}
        </ul>
        <IncluirAdvogado config={config} />
        <LinhaDoPadrao config={config} onAbrir={() => abrir(PADRAO_DO_ESCRITORIO)} />
        <WizardActions isFirst={isFirst} onBack={onBack} primaryLabel="Continuar" onPrimary={onNext} />
      </CardContent>
    </Card>
  );
}

/** O padrão do escritório, na lista: o que já está definido, num relance. */
function LinhaDoPadrao({ config, onAbrir }: { config: Configuracao; onAbrir: () => void }) {
  const endereco = config.advogado_endereco;
  // O mesmo critério dos cartões (endereço completo = logradouro, cidade e UF).
  const enderecoOk = !!(endereco?.logradouro && endereco.cidade && endereco.uf);
  const honorarios = temPreco(config.honorarios);
  const contrato = !!config.template_contrato_id;
  const procuracao = !!config.template_procuracao_id;
  const itens = [
    { chave: 'h', rotulo: honorarios ? 'Honorários' : 'Honorários: não definidos', ok: honorarios },
    {
      chave: 'm',
      rotulo: contrato && procuracao ? 'Modelos' : contrato || procuracao ? 'Modelos: falta um' : 'Modelos: não definidos',
      ok: contrato && procuracao,
    },
    { chave: 'e', rotulo: enderecoOk ? `${endereco?.cidade}/${endereco?.uf}` : 'Endereço: não definido', ok: enderecoOk },
  ];
  return (
    <Button
      type="button"
      variant="ghost"
      className="mt-2 h-auto min-h-14 w-full justify-between gap-3 rounded-lg border border-dashed bg-muted/40 px-3 py-2 text-left"
      onClick={onAbrir}
    >
      <span className="flex min-w-0 items-center gap-3">
        <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0">
          <span className="block font-medium">Padrão do escritório</span>
          <span className="flex flex-wrap gap-x-2 text-xs font-normal text-muted-foreground">
            {itens.map((item) => (
              <span key={item.chave} className="flex items-center gap-0.5">
                {item.ok && <Check className="h-3 w-3 text-emerald-600" />}
                {item.rotulo}
              </span>
            ))}
          </span>
        </span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </Button>
  );
}

function LinhaDoAdvogado({
  participante,
  indice,
  ativos,
  onAbrir,
  secretarioAtivo,
}: {
  participante: Participante;
  indice: number;
  ativos: Participante[];
  onAbrir: () => void;
  /** Desligado, o pronto ainda não atende ninguém. */
  secretarioAtivo: boolean;
}) {
  const queryClient = useQueryClient();
  const { showError } = useNotification();
  const [tirando, setTirando] = useState(false);

  const atualizar = (atualizada: Configuracao) => gravarConfigNoCache(queryClient, atualizada);
  const mover = useMutation({
    mutationFn: (destino: number) => {
      const ids = ativos.map((p) => p.usuario_id);
      ids.splice(indice, 1);
      ids.splice(destino, 0, participante.usuario_id);
      return reordenarParticipantes(ids);
    },
    onSuccess: atualizar,
    onError: (error) => notifyMutationError(error, showError),
  });
  const tirar = useMutation({
    mutationFn: () => removerParticipante(participante.usuario_id),
    onSuccess: (atualizada) => {
      atualizar(atualizada);
      setTirando(false);
    },
    onError: (error) => {
      setTirando(false);
      notifyMutationError(error, showError);
    },
  });

  const nome = nomeDoParticipante(participante);
  return (
    <li className="flex items-center gap-1 rounded-lg border bg-card">
      <Button
        type="button"
        variant="ghost"
        className="h-auto min-h-14 flex-1 justify-between gap-3 px-3 py-2 text-left"
        onClick={onAbrir}
      >
        <span className="min-w-0">
          <span className="block truncate font-medium">{nome}</span>
          {nome !== participante.nome && (
            <span className="block truncate text-xs text-muted-foreground">{participante.nome}</span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {participante.pronto ? (
            <Badge variant="outline" className="border-emerald-600/40 text-emerald-700 dark:text-emerald-400">
              {secretarioAtivo ? 'Atendendo' : 'Pronto'}
            </Badge>
          ) : (
            // O detalhe fica no perfil: aqui, só quantas.
            <Badge variant="outline" className="border-amber-600/40 text-amber-700 dark:text-amber-300">
              {participante.pendencias.length === 1 ? '1 pendência' : `${participante.pendencias.length} pendências`}
            </Badge>
          )}
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </span>
      </Button>
      <div className="flex shrink-0 items-center pr-1">
        {/* No computador, a ordem à vista; no celular, tudo no ⋮ (PADROES_UI_WEB). */}
        <span className="hidden sm:contents">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-11 w-11"
          aria-label={`Subir ${nome}`}
          disabled={indice === 0 || mover.isPending}
          onClick={() => mover.mutate(indice - 1)}
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-11 w-11"
          aria-label={`Descer ${nome}`}
          disabled={indice === ativos.length - 1 || mover.isPending}
          onClick={() => mover.mutate(indice + 1)}
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="icon" className="h-11 w-11" aria-label={`Mais ações de ${nome}`}>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem
              className="min-h-11 sm:hidden"
              disabled={indice === 0 || mover.isPending}
              onSelect={() => mover.mutate(indice - 1)}
            >
              <ArrowUp className="mr-2 h-4 w-4" />
              Subir
            </DropdownMenuItem>
            <DropdownMenuItem
              className="min-h-11 sm:hidden"
              disabled={indice === ativos.length - 1 || mover.isPending}
              onSelect={() => mover.mutate(indice + 1)}
            >
              <ArrowDown className="mr-2 h-4 w-4" />
              Descer
            </DropdownMenuItem>
            <DropdownMenuSeparator className="sm:hidden" />
            <DropdownMenuItem
              className="min-h-11 text-destructive focus:text-destructive sm:min-h-0"
              onSelect={() => setTirando(true)}
            >
              <UserMinus className="mr-2 h-4 w-4" />
              Tirar do Secretário
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <ConfirmDialog
        open={tirando}
        title={`Tirar ${nome} do Secretário?`}
        message={`O Secretário deixa de oferecer ${nome} aos clientes. Os atendimentos seguem como estão; incluir de novo devolve as áreas.`}
        confirmLabel="Tirar"
        confirmColor="error"
        loading={tirar.isPending}
        onConfirm={() => tirar.mutate()}
        onCancel={() => setTirando(false)}
      />
    </li>
  );
}

/** Incluir um advogado do escritório (sem aceite: ele recebe um aviso, §4.2). */
function IncluirAdvogado({ config }: { config: Configuracao }) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const escritorioId = config.escritorio_id ?? '';
  const { data: escritorio, isLoading } = useQuery({
    queryKey: queryKeys.escritorios.detail(escritorioId),
    queryFn: () => fetchEscritorio(escritorioId),
    enabled: !!escritorioId,
  });
  const participam = new Set((config.participantes ?? []).filter((p) => p.ativo).map((p) => p.usuario_id));
  const candidatos = (escritorio?.membros ?? []).filter((m) => !participam.has(m.usuario_id));

  const incluir = useMutation({
    mutationFn: (usuarioId: string) => incluirParticipante(usuarioId),
    onSuccess: (atualizada) => {
      gravarConfigNoCache(queryClient, atualizada);
      showSuccess('Incluído. Um aviso pede para conferir as áreas e os honorários.');
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  if (isLoading) return <Spinner label="Carregando os membros" />;
  if (candidatos.length === 0) return null;
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <UserPlus className="hidden h-4 w-4 text-muted-foreground sm:block" />
      <Combobox
        value=""
        onValueChange={(v) => v && incluir.mutate(v)}
        options={candidatos.map((m) => ({ value: m.usuario_id, label: m.usuario_nome, keywords: [m.usuario_email] }))}
        placeholder={incluir.isPending ? 'Incluindo…' : 'Incluir advogado do escritório'}
        searchPlaceholder="Buscar pelo nome"
        emptyMessage="Nenhum membro com esse nome."
        className="sm:flex-1"
      />
    </div>
  );
}
